/** @mui/material imports */
/** @mui/icons-material imports */
/** React imports */
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
/** App imports */
import { appContext } from '../../Contexts/AppContext';
import { errorHandler } from '../error/error-handler';
import { ValidatorProps } from '../../TypeScript/interfaces';
import NoMatch from '../../Components/404';
import { ActiveDrillholeContext } from './DrillholeValidator';
import { getImagesList } from '../authenticated-requests/images';
import { ImageListAPIResponse, ImageObject } from '@goldspotdiscoveries/types';

export const ActiveDrillholeImageContext = createContext<{
  verifiedDrillholeImage: ImageObject | null;
  previousDrillholeImageId: number | null;
  previousCleanedDrillholeImageId: number | null;
  nextDrillholeImageId: number | null;
  nextCleanedDrillholeImageId: number | null;
}>({
  verifiedDrillholeImage: null,
  previousDrillholeImageId: null,
  previousCleanedDrillholeImageId: null,
  nextDrillholeImageId: null,
  nextCleanedDrillholeImageId: null,
});

/**
 * Validate the drillhole id in the path and provide context
 *
 * Note: Assumes project id is verified
 *
 * @param props standard validator props
 * @returns child component with validation applie
 */
const DrillholeImageValidator: React.FC<ValidatorProps> = (
  props: React.PropsWithChildren<ValidatorProps>,
): JSX.Element => {
  const targetImageId = useParams().drillholeImageId;
  const { activeProject, setAppLoading, company } = useContext(appContext);
  const { verifiedDrillhole } = useContext(ActiveDrillholeContext);
  const [verifiedDrillholeImageId, setVerifiedDrillholeImageId] =
    useState<number>(-1);
  const [imageObject, setImageObject] = useState<ImageObject | null>(null);
  const [nextImageId, setNextImageId] = useState<number | null>(null);
  const [previousImageId, setPreviousImageId] = useState<number | null>(null);
  const [nextCleanedImageId, setNextCleanedImageId] = useState<number | null>(
    null,
  );
  const [previousCleanedImageId, setPreviousCleanedImageId] = useState<
    number | null
  >(null);
  const imageObjectRef = useRef<ImageObject | null>(null);

  // validate drillhole on init, when url changes, and when active project changes
  useEffect(() => {
    if (activeProject?.id && verifiedDrillhole?.id) {
      const imageId: number = Number(targetImageId);
      const drillholeId: number = verifiedDrillhole.id;
      const projectId: string = activeProject.id as string;
      setNextCleanedImageId(null);
      setNextImageId(null);
      setPreviousCleanedImageId(null);
      setPreviousImageId(null);
      setImageObject(null);
      imageObjectRef.current = null;
      validateImageId(projectId, drillholeId, imageId);
    }
  }, [activeProject, verifiedDrillhole, targetImageId]);

  const validateImageId = (
    projectId: string,
    drillholeId: number,
    imageId: number,
  ): void => {
    setAppLoading(true);
    getImagesList({
      company,
      project: projectId,
      drillholeId: `${drillholeId}`,
      query_params: {
        next_offset_token: imageId,
      },
    })
      .then((imageList: ImageListAPIResponse) => {
        for (let i = 0; i < imageList.items.length; i++) {
          if (imageList.items[i].id === imageId) {
            imageObjectRef.current = imageList.items[i];
            setImageObject(imageList.items[i]);
            setNextImageId(imageList.items[i + 1]?.id || null);
            for (let j = i + 1; j < imageList.items.length; j++) {
              if (imageList.items[j]?.masksPath) {
                setNextCleanedImageId(imageList.items[j].id);
                break;
              }
            }
            setPreviousImageId(imageList.items[i - 1]?.id || null);
            for (let j = i - 1; j >= 0; j--) {
              if (imageList.items[j]?.masksPath) {
                setPreviousCleanedImageId(imageList.items[j].id);
                break;
              }
            }
            break;
          }
        }
        if (imageObjectRef.current && imageObjectRef.current.id) {
          handleValidImageId(imageId);
        } else {
          handleInvalidImageId();
        }
      })
      .catch((err) => {
        handleInvalidImageId();
        errorHandler(err);
      });
  };

  const handleValidImageId = (imageId: number): void => {
    setVerifiedDrillholeImageId(imageId);
    setAppLoading(false);
  };

  const handleInvalidImageId = (): void => {
    setVerifiedDrillholeImageId(-2);
    setAppLoading(false);
  };

  return (
    <>
      {verifiedDrillholeImageId > -1 && (
        <ActiveDrillholeImageContext.Provider
          value={{
            verifiedDrillholeImage: imageObject,
            nextDrillholeImageId: nextImageId,
            previousDrillholeImageId: previousImageId,
            nextCleanedDrillholeImageId: nextCleanedImageId,
            previousCleanedDrillholeImageId: previousCleanedImageId,
          }}
        >
          {props.children}
        </ActiveDrillholeImageContext.Provider>
      )}
      {verifiedDrillholeImageId < -1 && <NoMatch />}
    </>
  );
};

export default DrillholeImageValidator;
